// ============================================================================
// COMPONENTE: CaseNextSteps.jsx — Próximos passos do caso
// Exibe os próximos marcos do processo em linguagem simples,
// baseado na etapa atual. Complementa o CaseProgressBar.
// ============================================================================

import React from 'react';
import { ArrowRight, CheckCircle2, Clock, Info } from 'lucide-react';

const STEPS = {
  pre_processual: [
    {
      label: 'Notificação extrajudicial',
      done: true,
      info: 'Enviamos uma notificação formal à parte contrária antes de entrar na Justiça, dando a chance de resolver sem processo.',
    },
    {
      label: 'Tentativa de acordo amigável',
      done: false,
      info: 'Tentamos resolver o problema diretamente com a outra parte, sem precisar de um juiz. É mais rápido e menos desgastante.',
    },
    {
      label: 'Protocolo da ação judicial',
      done: false,
      info: 'Formalizamos e enviamos o processo ao tribunal. A partir daqui o caso existe oficialmente na Justiça.',
    },
    {
      label: 'Citação da parte contrária',
      done: false,
      info: 'O tribunal notifica oficialmente a outra parte de que está sendo processada e que precisa apresentar sua defesa.',
    },
  ],
  peticao_inicial: [
    {
      label: 'Ação protocolada',
      done: true,
      info: 'O processo foi aberto e registrado no tribunal com um número oficial.',
    },
    {
      label: 'Citação da parte contrária',
      done: false,
      info: 'O tribunal notifica a outra parte de que está sendo processada e precisa se defender.',
    },
    {
      label: 'Resposta do réu / Contestação',
      done: false,
      info: 'A outra parte apresenta por escrito os seus argumentos de defesa ao juiz.',
    },
    {
      label: 'Audiência de mediação',
      done: false,
      info: 'Reunião conduzida por um mediador para tentar um acordo entre as partes antes do julgamento.',
    },
  ],
  em_andamento: [
    {
      label: 'Instrução processual',
      done: true,
      info: 'Fase em que todas as provas e documentos são apresentados e analisados pelo juiz.',
    },
    {
      label: 'Audiência de instrução',
      done: false,
      info: 'Reunião formal onde testemunhas podem ser ouvidas e provas apresentadas ao juiz pessoalmente.',
    },
    {
      label: 'Alegações finais',
      done: false,
      info: 'Cada lado apresenta seu argumento final por escrito antes da decisão do juiz.',
    },
    {
      label: 'Sentença do juiz',
      done: false,
      info: 'O juiz analisa tudo e decide quem tem razão e o que deve ser feito. É a decisão de primeira instância.',
    },
  ],
  recurso: [
    {
      label: 'Recurso protocolado',
      done: true,
      info: 'Solicitamos formalmente a revisão da decisão por um tribunal superior.',
    },
    {
      label: 'Análise pelo Tribunal',
      done: false,
      info: 'Um grupo de juízes (desembargadores) analisa o recurso e os argumentos de ambos os lados.',
    },
    {
      label: 'Acórdão (decisão colegiada)',
      done: false,
      info: 'A decisão final do grupo de juízes. Chamada acórdão porque é uma decisão tomada em conjunto.',
    },
    {
      label: 'Trânsito em julgado',
      done: false,
      info: 'A decisão é definitiva e não pode mais ser contestada. O processo está oficialmente encerrado.',
    },
  ],
};

function StepTooltip({ text }) {
  return (
    <span className="group relative inline-flex items-center ml-1 cursor-help align-middle">
      <Info className="w-3 h-3 text-gray-400 group-hover:text-[#C5A059] transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-0 mb-1.5 w-52 rounded-lg bg-[#20243B] px-2.5 py-2 text-white text-xs shadow-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100 z-50 leading-relaxed font-normal">
        {text}
        <span className="absolute top-full left-4 border-4 border-transparent border-t-[#20243B]" />
      </span>
    </span>
  );
}

export default function CaseNextSteps({ stage }) {
  const steps = STEPS[stage];
  if (!steps) return null;

  const firstPendingIdx = steps.findIndex(s => !s.done);

  return (
    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
        <ArrowRight className="w-3.5 h-3.5 text-[#C5A059]" />
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Próximos passos</span>
      </div>

      <div className="px-3 py-2.5">
        <ol className="relative border-l border-gray-200 ml-2 space-y-0">
          {steps.map((step, i) => {
            const isCurrent = i === firstPendingIdx;
            return (
              <li key={i} className="mb-0 ml-4">
                <span
                  className={`absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full ring-2 ring-white ${
                    step.done
                      ? 'bg-[#20243B]'
                      : isCurrent
                      ? 'bg-[#C5A059]'
                      : 'bg-gray-200'
                  }`}
                  style={{ marginTop: '3px' }}
                >
                  {step.done && <CheckCircle2 className="w-2 h-2 text-white" />}
                  {isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
                </span>
                <div className="py-1.5">
                  <span
                    className={`text-xs leading-tight ${
                      step.done
                        ? 'text-gray-400 line-through'
                        : isCurrent
                        ? 'font-bold text-[#20243B]'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </span>
                  <StepTooltip text={step.info} />
                  {isCurrent && (
                    <div>
                      <span className="inline-flex items-center gap-1 mt-0.5 text-[9px] font-bold text-[#C5A059] uppercase tracking-wide">
                        <Clock className="w-2.5 h-2.5" /> Em andamento
                      </span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
